import { inferBasecampRecordTypeFromDocument } from '@/lib/todo-id-from-path'

export type BasecampRecordType = 'todo' | 'todolist'

const DEFAULT_ACCOUNT_ID = '5744549'
export const BASECAMP_APP_HOST = 'app.basecamp.com'

/** User-facing Basecamp links use app.basecamp.com (BC5 browser URLs). */
export function normalizeBasecampWebHost(url: string): string {
  return url
    .replace(/https?:\/\/3\.basecamp\.com\//gi, `https://${BASECAMP_APP_HOST}/`)
    .replace(/https?:\/\/(?:www\.)?basecamp\.com\//gi, `https://${BASECAMP_APP_HOST}/`)
}

export function inferRecordTypeFromBasecampUrl(
  url: string | null | undefined,
  recordId: string | number,
): BasecampRecordType | null {
  if (!url) return null
  const id = String(recordId)
  if (new RegExp(`/todolists/${id}(?:[^0-9]|$)`).test(url)) return 'todolist'
  if (new RegExp(`/todos/${id}(?:[^0-9]|$)`).test(url)) return 'todo'
  return null
}

export function buildBasecampWebUrl(
  recordId: string | number,
  projectId: string | number,
  recordType: BasecampRecordType = 'todo',
  accountId: string = DEFAULT_ACCOUNT_ID,
): string {
  const segment = recordType === 'todolist' ? 'todolists' : 'todos'
  return `https://${BASECAMP_APP_HOST}/${accountId}/buckets/${projectId}/${segment}/${recordId}`
}

export function detectBasecampRecordType(
  basecampId: string | number,
  options?: {
    frontmatter?: Record<string, unknown> | null
    filePath?: string
    excerpt?: string
    apiRecordType?: BasecampRecordType | null
  },
): BasecampRecordType {
  if (options?.apiRecordType === 'todolist') return 'todolist'
  const fm = options?.frontmatter || {}
  if (fm.type === 'todolist' || fm.original_type === 'todolist') return 'todolist'
  if (String(fm.todolist_id) === String(basecampId) && !fm.todo_id) return 'todolist'
  if (options?.filePath || options?.excerpt) {
    return inferBasecampRecordTypeFromDocument(String(basecampId), {
      filePath: options.filePath,
      excerpt: options.excerpt,
      frontmatter: fm,
    })
  }
  // Note: a filePath-based fallback here would be unreachable dead code -
  // the branch above always returns whenever options.filePath is truthy.
  const fromUrl = inferRecordTypeFromBasecampUrl(
    String(fm.source_url || fm.url || ''),
    basecampId,
  )
  if (fromUrl) return fromUrl
  return 'todo'
}

/**
 * Pick or construct a Basecamp web URL with the correct todos vs todolists segment.
 */
export function resolveBasecampAppUrl(options: {
  recordId: string | number
  projectId?: string | number | null
  recordType?: BasecampRecordType
  accountId?: string
  candidateUrl?: string | null
}): string | null {
  const recordId = String(options.recordId)
  const projectId = options.projectId != null ? String(options.projectId) : null
  const accountId = options.accountId || DEFAULT_ACCOUNT_ID
  const candidateUrl =
    typeof options.candidateUrl === 'string' && options.candidateUrl.trim().length > 0
      ? options.candidateUrl.trim()
      : null

  const typeFromCandidate = inferRecordTypeFromBasecampUrl(candidateUrl, recordId)
  const recordType: BasecampRecordType =
    options.recordType === 'todolist' || typeFromCandidate === 'todolist' ? 'todolist' : 'todo'

  if (candidateUrl && typeFromCandidate === recordType) {
    return normalizeBasecampWebHost(candidateUrl)
  }

  if (recordType === 'todolist' && candidateUrl && typeFromCandidate === 'todo' && projectId) {
    return buildBasecampWebUrl(recordId, projectId, 'todolist', accountId)
  }

  if (projectId) {
    return buildBasecampWebUrl(recordId, projectId, recordType, accountId)
  }

  return candidateUrl ? normalizeBasecampWebHost(candidateUrl) : null
}

export function resolveExternalBasecampUrlForDocument(options: {
  basecampId: string
  projectId?: string | null
  frontmatter?: Record<string, unknown> | null
  filePath?: string | null
  sourceUrl?: string | null
}): string | null {
  const candidate =
    (typeof options.sourceUrl === 'string' && options.sourceUrl.trim()) ||
    (typeof options.frontmatter?.source_url === 'string' && options.frontmatter.source_url) ||
    (typeof options.frontmatter?.url === 'string' && options.frontmatter.url) ||
    null

  if (!options.projectId) {
    return candidate ? normalizeBasecampWebHost(candidate) : null
  }

  const recordType = detectBasecampRecordType(options.basecampId, {
    frontmatter: options.frontmatter,
    filePath: options.filePath ?? undefined,
  })

  return resolveBasecampAppUrl({
    recordId: options.basecampId,
    projectId: options.projectId,
    recordType,
    candidateUrl: candidate,
  })
}
